import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Ban, Download, Trash2, X } from 'lucide-react';
import { GALLERY_SELECTION_TOOLBAR } from '@/pages/ipe/workbench/constants/gallerySelectionToolbar';

/**
 * 会盖住 / 与底部多选栏冲突的快捷功能弹窗根节点。
 * 任意命中即隐藏悬浮栏（含未在页面侧显式传 visible=false 的情况）。
 */
export const IPE_SELECTION_BAR_BLOCKING_OVERLAY_SELECTOR = [
  '.funding-modal',
  '.detail-tool-bridge-modal__backdrop',
  '.ai-cw-tpl-picker-layer',
  '.goods-studio-create-modal-layer',
  '.smart-cutout-refine-modal',
  '.ai-copywriting-enlarge-modal__backdrop',
  '.funding-image-space-preview-overlay',
  '[data-ipe-blocks-selection-bar]',
].join(',');

function isElementVisible(el) {
  if (!(el instanceof Element)) return false;
  if (el.hasAttribute('hidden')) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }
  return true;
}

export function hasBlockingSelectionBarOverlay(root = typeof document !== 'undefined' ? document : null) {
  if (!root?.querySelectorAll) return false;
  const nodes = root.querySelectorAll(IPE_SELECTION_BAR_BLOCKING_OVERLAY_SELECTOR);
  for (let i = 0; i < nodes.length; i += 1) {
    const el = nodes[i];
    if (el.closest?.('.ipe-gallery-selection-bar')) continue;
    if (isElementVisible(el)) return true;
  }
  return false;
}

function useBlockingOverlayOpen() {
  const [blocked, setBlocked] = useState(() => hasBlockingSelectionBarOverlay());

  useEffect(() => {
    let rafId = 0;
    const sync = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        setBlocked(hasBlockingSelectionBarOverlay());
      });
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  return blocked;
}

/**
 * 图片网格多选底部浮动工具栏（全局规范）
 *
 * 固定：全选、删除、下载（主区）+ 取消选择（右侧）
 * 扩展：children
 * 任意快捷功能弹窗打开时自动隐藏，避免叠在弹窗底部。
 */
export default function IpeGallerySelectionBar({
  visible = false,
  countLabel,
  onSelectAll,
  onClearSelection,
  clearLabel = GALLERY_SELECTION_TOOLBAR.cancelLabel,
  showSelectAll = true,
  onDelete,
  deleteBusy = false,
  deleteDisabled = false,
  showDelete = true,
  onDisable,
  onDownload,
  downloadBusy = false,
  downloadDisabled = false,
  disableDisabled = false,
  showDisable = true,
  showDownload = true,
  children,
}) {
  const { t } = useTranslation('workbench');
  const overlayBlocks = useBlockingOverlayOpen();
  const canDelete = showDelete && typeof onDelete === 'function';
  const canDisable = showDisable && typeof onDisable === 'function';
  const canDownload = showDownload && typeof onDownload === 'function';

  if (!visible || overlayBlocks) return null;

  return createPortal(
    <div className="ipe-gallery-selection-bar" role="toolbar" aria-label={t('spaceBoard.selectImagesAria')}>
      <div className="ipe-gallery-selection-bar__main">
        <span className="ipe-gallery-selection-bar__count">{countLabel}</span>
        {showSelectAll ? (
          <button
            type="button"
            className="ipe-gallery-selection-bar__action"
            onClick={onSelectAll}
          >
            {t('studioChrome.selectAll')}
          </button>
        ) : null}
        {children}
        {canDelete ? (
          <button
            type="button"
            className="ipe-gallery-selection-bar__action"
            disabled={deleteDisabled || deleteBusy}
            onClick={onDelete}
          >
            <Trash2 size={16} aria-hidden="true" />
            {deleteBusy ? '删除中…' : GALLERY_SELECTION_TOOLBAR.deleteLabel}
          </button>
        ) : null}
        {canDisable ? (
          <button
            type="button"
            className="ipe-gallery-selection-bar__action"
            disabled={disableDisabled}
            onClick={onDisable}
          >
            <Ban size={16} aria-hidden="true" />
            {GALLERY_SELECTION_TOOLBAR.disableLabel}
          </button>
        ) : null}
        {canDownload ? (
          <button
            type="button"
            className="ipe-gallery-selection-bar__action ipe-gallery-selection-bar__action--download"
            disabled={downloadDisabled || downloadBusy}
            onClick={onDownload}
          >
            <Download size={16} aria-hidden="true" />
            {downloadBusy ? '下载中…' : GALLERY_SELECTION_TOOLBAR.downloadLabel}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        className="ipe-gallery-selection-bar__cancel"
        onClick={onClearSelection}
        aria-label={clearLabel}
      >
        <X size={16} />
        {clearLabel}
      </button>
    </div>,
    document.body,
  );
}
